import { Box } from '@material-ui/core';

import { ValidationStep, ValidationStepStatus } from '@/components/modules/DocumentsValidation/types/ValidationStep';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { HorizontalProgressBar } from '@/components/shared/components/HorizontalProgressBar';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useScreen } from '@/hooks/useScreen';

interface DocumentationStepTrackerProps {
  step: ValidationStepStatus;
  steps: ValidationStepStatus[];
}

export const DocumentationStepTracker = ({ step, steps }: DocumentationStepTrackerProps) => {
  const { md } = useScreen();

  const currentStepIndex = steps.findIndex((s) => s.step === step.step) + 1;
  const stepLength = steps.length;
  const progress = (currentStepIndex / stepLength) * 100;

  const getStepStatus = (step: ValidationStepStatus, index: number) => {
    if (step.step === ValidationStep.CONFIRMATION && currentStepIndex === steps.length) {
      const anyStepInIncidence = steps.some((s) => s.status === 'incidence');
      return anyStepInIncidence ? 'incidence' : 'completed';
    }

    if (index < currentStepIndex) {
      return step.status;
    }

    if (index === currentStepIndex) {
      return 'active';
    }

    return 'pending';
  };

  return (
    <Box>
      <Font variant="body2" color="textSecondary">
        <Text tId={'STEP_X_OF_Y'} dynamicDataTexts={[String(currentStepIndex), String(stepLength)]} />
      </Font>
      <HorizontalProgressBar progressValue={progress} />
      {md && (
        <FlexColumn pt={1}>
          {steps.map((step, index) => {
            index += 1;
            const status = getStepStatus(step, index);
            return <Step key={step.step} step={{ ...step, status }} />;
          })}
        </FlexColumn>
      )}
    </Box>
  );
};

const Step = ({ step }: { step: ValidationStepStatus }) => {
  return (
    <FlexRow style={{ opacity: step.status === 'pending' ? 0.6 : 1 }}>
      <Box flexShrink={0}>
        <Box
          height={20}
          width={20}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          color={
            step.status === 'completed' ? 'success.main' : step.status === 'incidence' ? 'error.main' : 'text.secondary'
          }
        >
          {step.status === 'active' ? (
            <Box height={6} width={6} bgcolor={'text.secondary'} borderRadius={'100%'} />
          ) : (
            <Icon size={16}>
              {step.status === 'completed' ? 'check' : step.status === 'incidence' ? 'close' : 'hourglass_empty'}
            </Icon>
          )}
        </Box>
      </Box>
      <Font variant="body2" color="textPrimary">
        <Text strong={step.status === 'active'} tId={`DOCUMENTS_VALIDATION_STEP_${step.step}`} />
      </Font>
    </FlexRow>
  );
};
